// Product Form Fields Module - 폼 필드 관리 (가격, 설명, 거래 옵션)
// 메인 product_form_controller.js에서 import하여 사용

import { getI18nMessage } from '../../lib/i18n_helper'
import { showInfo } from '../../lib/platform_toast'

export const FieldsMixin = {
  // 설명 글자 수 카운터 업데이트
  updateDescriptionCount(event) {
    if (!this.hasDescriptionCountTarget) return

    const textarea = event?.currentTarget || this.element.querySelector("textarea[name='product[description]']")
    if (!textarea) return

    const length = textarea.value.length
    const max = parseInt(textarea.getAttribute("maxlength"), 10) || 1000
    this.descriptionCountTarget.textContent = `${length} / ${max}`
  },

  showAiCopyHint() {
    const message = getI18nMessage(
      this.element,
      'fieldsAiHint',
      'AI 草稿功能即將推出。請稍候！'
    )
    showInfo(message)
  },

  // --- Pricing helpers ----------------------------------------------------
  toggleFreeMode(event) {
    if (!this.hasPriceInputTarget || !this.hasFreeToggleTarget) return

    const toggle = event.currentTarget
    const willActivate = !toggle.classList.contains("is-active")
    const field = this.priceInputTarget

    if (willActivate) {
      // Remember previous price if greater than zero
      const current = parseFloat(field.value || "0")
      if (current > 0) {
        this.previousPrice = field.value
      }

      field.value = 0
      field.setAttribute("readonly", "readonly")
      field.classList.add("bg-crumb-100")
      toggle.classList.add("is-active")

      // Show free mode overlay
      if (this.hasFreeOverlayTarget) {
        this.freeOverlayTarget.classList.remove("hidden")
      }

      // Disable negotiable when free (nothing to negotiate)
      this.disableNegotiable()
    } else {
      field.removeAttribute("readonly")
      field.classList.remove("bg-crumb-100")
      toggle.classList.remove("is-active")

      // Hide free mode overlay
      if (this.hasFreeOverlayTarget) {
        this.freeOverlayTarget.classList.add("hidden")
      }

      if (this.previousPrice && parseFloat(this.previousPrice) > 0) {
        field.value = this.previousPrice
      } else {
        field.value = ""
      }
      field.focus()

      // Re-enable negotiable when not free
      this.enableNegotiable()
    }

    this.syncFreeToggle()
    // The value changed in code: let listeners that follow typing (validation, the wizard live card) see it too.
    field.dispatchEvent(new Event("input", { bubbles: true }))
  },

  syncFreeToggle() {
    if (!this.hasPriceInputTarget || !this.hasFreeToggleTarget) return

    const field = this.priceInputTarget
    const toggle = this.freeToggleTarget
    const rawValue = (field.value || "").toString().trim()
    const hasValue = rawValue.length > 0
    const price = hasValue ? parseFloat(rawValue) : null
    const isFree = hasValue && !Number.isNaN(price) && price === 0

    if (isFree) {
      field.setAttribute("readonly", "readonly")
      field.classList.add("bg-crumb-100")
      toggle.classList.add("is-active")

      // Show free mode overlay
      if (this.hasFreeOverlayTarget) {
        this.freeOverlayTarget.classList.remove("hidden")
      }

      // Disable negotiable when free
      this.disableNegotiable()
    } else {
      field.removeAttribute("readonly")
      field.classList.remove("bg-crumb-100")
      toggle.classList.remove("is-active")

      // Hide free mode overlay
      if (this.hasFreeOverlayTarget) {
        this.freeOverlayTarget.classList.add("hidden")
      }

      // Re-enable negotiable when not free
      this.enableNegotiable()
    }
  },

  updateNegotiable(event) {
    const checkbox = event.currentTarget
    const label = checkbox.closest("label.toggle-chip")
    if (!label) return

    if (checkbox.checked) {
      label.classList.add("is-active")
    } else {
      label.classList.remove("is-active")
    }
  },

  initializeNegotiableChip() {
    const checkbox = this.element.querySelector("input[name='product[negotiable]']")
    if (!checkbox) return
    const label = checkbox.closest("label.toggle-chip")
    if (!label) return

    // Check if free mode is active (price = 0, but NOT empty/new product)
    const priceInput = this.hasPriceInputTarget ? this.priceInputTarget : null
    const rawValue = priceInput?.value?.trim() || ""
    const isFree = rawValue !== "" && parseFloat(rawValue) === 0

    if (isFree) {
      // Disable negotiable for free products
      checkbox.checked = false
      checkbox.disabled = true
      label.classList.add("is-disabled")
      label.classList.remove("is-active")
    } else if (checkbox.checked) {
      label.classList.add("is-active")
    } else {
      label.classList.remove("is-active")
    }
  },

  syncTradeOptions() {
    const checkboxes = this.element.querySelectorAll("input[name='product[trade_direct]'], input[name='product[trade_delivery]']")
    checkboxes.forEach((checkbox) => {
      const wrapper = checkbox.closest("label.trade-option")
      if (!wrapper) return

      if (checkbox.checked) {
        wrapper.classList.add("is-active")
      } else {
        wrapper.classList.remove("is-active")
      }
    })
  },

  // --- Negotiable disable/enable helpers (for free mode) ---
  disableNegotiable() {
    const checkbox = this.element.querySelector("input[name='product[negotiable]']")
    const label = checkbox?.closest("label.toggle-chip")

    if (checkbox) {
      checkbox.checked = false
      checkbox.disabled = true
    }
    if (label) {
      label.classList.remove("is-active")
      label.classList.add("is-disabled")
    }
  },

  enableNegotiable() {
    const checkbox = this.element.querySelector("input[name='product[negotiable]']")
    const label = checkbox?.closest("label.toggle-chip")

    if (checkbox) {
      checkbox.disabled = false
    }
    if (label) {
      label.classList.remove("is-disabled")
    }
  }
}
